/**
 * Shell-level navigation state. The widget has TWO layout modes:
 *
 *   - **List view** (`showTabs: true`)  — header chrome + tab nav
 *     visible. Visitor is browsing an index: home cards, help
 *     categories, news feed, profile editor.
 *
 *   - **Drill-in** (`showTabs: false`) — sticky header with a back
 *     button; bottom nav hidden so the surface feels focused.
 *     Visitor is reading an article, in a conversation, or viewing
 *     a single news post.
 *
 * Routes are flat values (not nested) so the shell can pattern-match
 * with a single switch. The `fromCategoryId` on `help-article` lets
 * us walk back UP one level to the category-articles view rather
 * than the categories index, matching how the user expects Back to
 * land them where they came from.
 */

import type { WidgetFeatures, WidgetTab } from "./widget-config";

export type WidgetRoute =
  | { kind: "home" }
  | { kind: "messages" }
  | {
      /** A single topic thread's conversation, drilled in from the Messages
       *  thread list (multi-topic threads). `chatId` opens an
       *  existing thread's history + SSE; a freshly-minted `threadKey` with no
       *  `chatId` yet is a "new question" thread (its chatId arrives on the
       *  first send ack). Omit both → the anchor / main thread. */
      kind: "messages-thread";
      chatId?: string;
      threadKey?: string | null;
      /** "Resolving the latest thread" — the resume landing after a reload. We
       *  drill into the conversation view immediately (drill-in chrome + a
       *  loading skeleton) while `visitorThreads` resolves, then swap in the
       *  latest thread's `chatId`/`threadKey` (or fall back to the list when the
       *  visitor has no thread yet) — instead of flashing the list then jumping. */
      pendingLatest?: boolean;
      /** A new regular thread started on a page: it shows this MESSAGE slot's text client-side (015). */
      welcomeSlotId?: string;
      /** An in-place re-route (a rejected slot send moved the visitor to the slot's local thread): keep the
       *  layer key of the thread it replaced, so the tab stays mounted (no exit / enter, no second welcome). */
      keyAs?: string;
    }
  | { kind: "help" }
  | {
      kind: "help-category";
      categoryId: string;
      /** Folder display metadata threaded through the route so the
       *  drill-in view can render the header (title + description +
       *  count) without a per-folder BE round-trip — the parent
       *  always has these fields in hand the moment it navigates.
       *  Optional so deep-link entries (no parent state) still
       *  function — the header just degrades to nothing in that case. */
      categoryName?: string;
      categoryDescription?: string;
      categoryArticleCount?: number;
      /** The exact view the visitor drilled in FROM. Drives "Back" so it
       *  returns to the origin surface (home / messages / a parent category)
       *  rather than a fixed section index. See `withOrigin` + `popRoute`. */
      origin?: WidgetRoute;
    }
  | {
      kind: "help-article";
      slug: string;
      /** Path the visitor took to land here. Legacy back hint, still used to
       *  reconstruct a category parent when no `origin` is set (deep links). */
      from: "search" | "category";
      fromCategoryId?: string;
      /** Locale override for the article query. Set when the visitor
       *  picks a different language in the below-article selector —
       *  the variant is identified by `slug` + this `locale` (the BE's
       *  visitor lookup is keyed on the pair). Falls back to the
       *  widget locale when unset. */
      locale?: string;
      /** The exact view the visitor drilled in FROM (home, messages, help,
       *  a category, another article…). Takes precedence over `from` for
       *  "Back". See `withOrigin` + `popRoute`. */
      origin?: WidgetRoute;
    }
  | { kind: "news" }
  | { kind: "news-article"; id: string; origin?: WidgetRoute }
  | { kind: "profile" };

/**
 * Map a route to whether the bottom-nav + tab chrome should render.
 * List routes show tabs; drill-ins hide them.
 */
export function routeShowsTabs(route: WidgetRoute): boolean {
  return (
    route.kind === "home" ||
    // The Messages tab now lands on the thread LIST (a list surface, tabs
    // shown); the single-thread conversation is `messages-thread` (a drill-in,
    // tabs hidden).
    route.kind === "messages" ||
    route.kind === "help" ||
    route.kind === "news" ||
    route.kind === "profile"
  );
}

/**
 * Map a drill-in route to the route it should pop back to. The
 * `features` flags decide where Messages-back lands when the
 * operator has disabled the Home tab — falling through to the next
 * available list surface so the back button never points at a
 * disabled destination.
 */
export function popRoute(route: WidgetRoute, features: WidgetFeatures): WidgetRoute {
  switch (route.kind) {
    case "messages": {
      if (features.home) return { kind: "home" };
      if (features.help) return { kind: "help" };
      if (features.news) return { kind: "news" };
      if (features.profile) return { kind: "profile" };
      // Only messenger enabled → there's nowhere to go; stay put.
      return { kind: "messages" };
    }
    case "help-category":
      // Back to wherever we drilled in from (home, a parent category…),
      // else the help index.
      return route.origin ?? { kind: "help" };
    case "help-article":
      // `origin` (the exact view we came from) wins; fall back to the legacy
      // category hint, then the help index — so deep links still resolve.
      if (route.origin) return route.origin;
      if (route.from === "category" && route.fromCategoryId) {
        return { kind: "help-category", categoryId: route.fromCategoryId };
      }
      return { kind: "help" };
    case "news-article":
      return route.origin ?? { kind: "news" };
    case "messages-thread":
      // Back from ANY thread is the Messages thread list, however it opened.
      return { kind: "messages" };
    default:
      return { kind: "home" };
  }
}

/**
 * Attach a back-origin to a drill-in navigation: remember the view the visitor
 * is leaving (`origin`) so `popRoute` can return them there. Tab-level routes
 * (home/messages/help/news/profile) don't carry an origin — switching tabs is
 * a fresh context, not a drill-in — so they pass through unchanged.
 */
export function withOrigin(next: WidgetRoute, origin: WidgetRoute): WidgetRoute {
  if (
    next.kind === "help-article" ||
    next.kind === "news-article" ||
    next.kind === "help-category"
  ) {
    return { ...next, origin };
  }
  return next;
}

/**
 * Open a RELATED article from an article: swap the current article for the
 * new one (same kind) and keep the chain's original back-origin, so Back from
 * any article of the chain returns to where the chain started (News list,
 * Help category, Home, Messenger) instead of the previous related article.
 * From any other route it behaves like `withOrigin`.
 */
export function openRelatedArticle(next: WidgetRoute, prev: WidgetRoute): WidgetRoute {
  if (next.kind === "news-article" && prev.kind === "news-article") {
    return { ...next, origin: prev.origin };
  }
  if (next.kind === "help-article" && prev.kind === "help-article") {
    return {
      ...next,
      origin: prev.origin,
      // Deep-link chain (no origin): keep the legacy category hint.
      ...(prev.origin ? {} : { from: prev.from, fromCategoryId: prev.fromCategoryId }),
    };
  }
  return withOrigin(next, prev);
}

/**
 * Map a route back to the tab id that owns it — used by the bottom
 * nav to highlight the right tab when we ARE on a list route, and
 * by the route reducer when the visitor taps a tab.
 */
export function routeToTab(route: WidgetRoute): WidgetTab {
  switch (route.kind) {
    case "home":
      return "home";
    case "messages":
    case "messages-thread":
      return "messages";
    case "help":
    case "help-category":
    case "help-article":
      return "help";
    case "news":
    case "news-article":
      return "news";
    case "profile":
      return "profile";
  }
}

/**
 * Checks whether a tab is enabled by the operator's feature flags.
 * Maps the `messages` tab id to the `messenger` flag (legacy naming
 * — the flag is named after the channel, the tab after the noun).
 */
export function isTabEnabled(tab: WidgetTab, features: WidgetFeatures): boolean {
  if (tab === "home") return features.home;
  if (tab === "messages") return features.messenger;
  if (tab === "help") return features.help;
  if (tab === "news") return features.news;
  return !!features.profile;
}

/**
 * Count of feature-enabled tabs. When this is 1 the widget runs
 * effectively as a single-surface app — shell hides the bottom nav
 * and any back arrow that would otherwise pop "up" to a tab the
 * operator has disabled.
 */
export function enabledTabCount(features: WidgetFeatures): number {
  return (
    (features.home ? 1 : 0) +
    (features.messenger ? 1 : 0) +
    (features.help ? 1 : 0) +
    (features.news ? 1 : 0) +
    (features.profile ? 1 : 0)
  );
}

/**
 * Picks the tab the visitor should land on given the operator's
 * `defaultTab` config + `features` flag set:
 *
 *   1. If `defaultTab` is enabled → use it.
 *   2. Otherwise walk the canonical order (home → messages → help
 *      → news → profile) and pick the first enabled tab.
 *   3. If NOTHING is enabled (shouldn't happen — at minimum
 *      messenger is always on) → fall back to `defaultTab` anyway.
 */
export function pickInitialTab(defaultTab: WidgetTab, features: WidgetFeatures): WidgetTab {
  if (isTabEnabled(defaultTab, features)) return defaultTab;
  const order: WidgetTab[] = ["home", "messages", "help", "news", "profile"];
  for (const t of order) {
    if (isTabEnabled(t, features)) return t;
  }
  return defaultTab;
}
